/**
 * FileRequiredEmptyState Component
 *
 * A reusable empty state component for pages that require a file to be loaded.
 * Provides consistent messaging, visual design, and call-to-action across the application.
 */

import { useNavigate } from 'react-router-dom';
import { useFileStore } from '@/storage';

export type EmptyStateReason = 
  | 'no-file'           // No IDF file loaded
  | 'loading-schema'    // Schema is loading
  | 'schema-error'      // Schema failed to load
  | 'no-objects'        // File loaded but no objects to display
  | 'no-schedules';     // File loaded but no schedules

interface FileRequiredEmptyStateProps {
  /** The reason for the empty state */
  reason: EmptyStateReason;
  /** Optional custom title (overrides default) */
  title?: string;
  /** Optional custom message (overrides default) */
  message?: string;
  /** Optional custom icon (overrides default) */
  icon?: React.ReactNode;
  /** Action button configuration */
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Optional secondary action (e.g., "Go to Editor") */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  /** Error message (for schema-error reason) */
  error?: string | null;
  /** Loading state (for loading-schema reason) */
  isLoading?: boolean;
}

/**
 * Default configurations for each empty state reason
 */
const DEFAULTS: Record<EmptyStateReason, {
  title: string;
  message: string;
  icon: (className: string) => React.ReactNode;
  showAction: boolean;
  actionLabel?: string;
}> = {
  'no-file': {
    title: 'No File Loaded',
    message: 'Load an IDF file to get started. You can import an existing file, create a new one, or open an example.',
    icon: (className) => (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    showAction: true,
    actionLabel: 'Go to Editor',
  },
  'loading-schema': {
    title: 'Loading Schema',
    message: 'Please wait while the EnergyPlus IDD schema loads...',
    icon: (className) => (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    ),
    showAction: false,
  },
  'schema-error': {
    title: 'Schema Load Failed',
    message: 'Unable to load the EnergyPlus schema. Please try again.',
    icon: (className) => (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    showAction: true,
    actionLabel: 'Retry',
  },
  'no-objects': {
    title: 'No Objects Yet',
    message: 'This file is empty. Create your first object to get started.',
    icon: (className) => (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    showAction: true,
    actionLabel: 'Create Object',
  },
  'no-schedules': {
    title: 'No Schedules Found',
    message: 'This file doesn\'t contain any schedules. Create a new schedule or import from a template.',
    icon: (className) => (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </svg>
    ),
    showAction: true,
    actionLabel: 'Create Schedule',
  },
};

/**
 * FileRequiredEmptyState Component
 */
export function FileRequiredEmptyState({
  reason,
  title,
  message,
  icon,
  action,
  secondaryAction,
  error,
  isLoading,
}: FileRequiredEmptyStateProps) {
  const navigate = useNavigate();
  const setImportDialogOpen = useFileStore((state) => state.setImportDialogOpen);
  const config = DEFAULTS[reason];

  // Determine if we should show loading animation
  const showLoading = reason === 'loading-schema' && (isLoading ?? true);

  // Determine icon to use
  const iconElement = icon ?? config.icon(
    showLoading 
      ? 'h-20 w-20 animate-spin text-muted-foreground/50'
      : reason === 'schema-error'
      ? 'h-20 w-20 text-destructive/50'
      : 'h-20 w-20 text-muted-foreground/50'
  );

  // Determine title and message
  const displayTitle = title ?? config.title;
  const displayMessage = message ?? config.message;

  // Determine primary action
  const primaryAction = action ?? (config.showAction ? {
    label: config.actionLabel!,
    onClick: reason === 'no-file' 
      ? () => navigate('/')
      : () => {}, // Default no-op, should be overridden by caller
  } : undefined);

  // Default secondary action for no-file states
  const defaultSecondaryAction = reason === 'no-file' ? {
    label: 'Import File',
    onClick: () => {
      setImportDialogOpen(true);
    },
  } : undefined;

  const displaySecondaryAction = secondaryAction ?? defaultSecondaryAction;

  return (
    <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
      {/* Icon */}
      <div className="mb-4">
        {iconElement}
      </div>

      {/* Title */}
      <h2 className="text-lg font-medium text-foreground">
        {displayTitle}
      </h2>

      {/* Message */}
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        {displayMessage}
      </p>

      {/* Error details (for schema-error) */}
      {reason === 'schema-error' && error && (
        <p className="mt-2 max-w-sm text-xs text-destructive">
          {error}
        </p>
      )}

      {/* Actions */}
      {!showLoading && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          {primaryAction && (
            <button
              onClick={primaryAction.onClick}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {primaryAction.label}
            </button>
          )}
          {displaySecondaryAction && (
            <button
              onClick={displaySecondaryAction.onClick}
              className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              {displaySecondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
